import Fade from '@mui/material/Fade';
import { useTheme } from '@mui/material/styles';
import { TooltipContentProps } from 'recharts';

interface TooltipSummaryRow {
  label: string;
  value: number;
  color?: string;
}

interface Props extends TooltipContentProps<number, string> {
  yFormatter: (value: number) => string;
  xFormatter: (value: string | number) => string;
  unit?: string;
  showTotal?: boolean;
  showNegativeSign?: boolean;
  totals?: { label: string; prefixes: string[]; alwaysShow?: boolean }[];
  extraRows?: TooltipSummaryRow[];
}

export function CustomTooltip({
  active,
  payload,
  label,
  yFormatter,
  xFormatter,
  unit,
  showTotal = false,
  showNegativeSign = false,
  totals,
  extraRows = [],
}: Props) {
  const theme = useTheme();
  const fontFamily = theme.typography.fontFamily;
  if (!active || !payload || payload.length === 0) return null;
  const rawX: string | number | undefined =
    label ?? payload?.[0]?.payload?.ts ?? payload?.[0]?.payload?.timestamp;

  const header =
    typeof rawX === 'number' || typeof rawX === 'string'
      ? xFormatter(rawX)
      : '';

  // Calculate total if showTotal is enabled
  const total = showTotal
    ? payload.reduce((sum, p) => sum + (Number(p.value) || 0), 0)
    : 0;
  const normalizedTotals = (totals ?? [])
    .map((entry) => {
      const matching = payload.filter((item) =>
        entry.prefixes.some(
          (prefix) =>
            typeof item.name === 'string' && item.name.startsWith(prefix)
        )
      );
      if (
        matching.length === 0 ||
        (matching.length === 1 && !entry.alwaysShow)
      ) {
        return null;
      }
      const value = matching.reduce(
        (sum, item) => sum + (Number(item.value) || 0),
        0
      );
      return Number.isFinite(value) ? { label: entry.label, value } : null;
    })
    .filter((entry): entry is TooltipSummaryRow => entry != null);
  const summaryRows = [...normalizedTotals, ...extraRows];

  return (
    <Fade
      in={active}
      timeout={{ enter: 300, exit: 300 }}
      style={{ transitionDelay: active ? '300ms' : '0ms' }}
    >
      <div
        style={{
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 6,
          overflow: 'hidden',
          boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
          paddingBottom: 6,
          fontFamily,
        }}
      >
        <div
          style={{
            marginBottom: 6,
            color: theme.palette.text.primary,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.grey[200],
            paddingTop: 6,
            paddingBottom: 6,
            paddingLeft: 10,
            paddingRight: 10,
          }}
        >
          {header}
        </div>
        <div style={{ display: 'grid', gap: 8 }}>
          {payload.map((p, idx) => (
            <div
              key={idx}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                paddingLeft: 10,
                paddingRight: 10,
              }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '100%',
                  backgroundColor: p.color,
                  display: 'inline-block',
                }}
              />
              <span style={{ color: theme.palette.text.primary }}>
                {p.name}:
              </span>
              <span style={{ fontWeight: 700 }}>
                {p.value == null ? (
                  <span style={{ color: theme.palette.text.disabled }}>
                    N/A
                  </span>
                ) : (
                  <>
                    {showNegativeSign && Number(p.value) < 0 ? '-' : ''}
                    {yFormatter(
                      showNegativeSign
                        ? Math.abs(Number(p.value))
                        : Number(p.value)
                    )}
                    {unit ? ' ' : ''}
                    {unit}
                  </>
                )}
              </span>
            </div>
          ))}
          {showTotal && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                paddingLeft: 10,
                paddingRight: 10,
                paddingTop: 6,
                marginTop: 2,
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  display: 'inline-block',
                }}
              />
              <span>Total:</span>
              <span style={{ fontWeight: 700 }}>
                {showNegativeSign && total < 0 ? '-' : ''}
                {yFormatter(showNegativeSign ? Math.abs(total) : total)}
                {unit ? ' ' : ''}
                {unit}
              </span>
            </div>
          )}
          {summaryRows.map((entry, idx) => (
            <div
              key={entry.label}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                paddingLeft: 10,
                paddingRight: 10,
                ...(idx === 0 && !showTotal
                  ? {
                      paddingTop: 6,
                      marginTop: 2,
                      borderTop: `1px solid ${theme.palette.divider}`,
                    }
                  : {}),
              }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '100%',
                  backgroundColor: entry.color ?? 'transparent',
                  display: 'inline-block',
                }}
              />
              <span>{entry.label}:</span>
              <span style={{ fontWeight: 700 }}>
                {showNegativeSign && entry.value < 0 ? '-' : ''}
                {yFormatter(
                  showNegativeSign ? Math.abs(entry.value) : entry.value
                )}
                {unit ? ' ' : ''}
                {unit}
              </span>
            </div>
          ))}
        </div>
      </div>
    </Fade>
  );
}
